'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { ChevronDown, Search, X } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { useDebouncedCallback } from '@/utilities/useDebounce'
import {
  buildOrgProgramSwitcherViewModel,
  type SwitcherOrganization,
  type SwitcherSelection,
} from '@/utilities/dashboard/orgProgramSwitcherTree'
import { OrgProgramSwitcherDesktop } from './OrgProgramSwitcherDesktop'
import { OrgProgramSwitcherMobile } from './OrgProgramSwitcherMobile'
import {
  EMPTY_SCROLL_AFFORDANCE,
  FOCUS_VISIBLE_RING,
  getScrollAffordance,
  ROW_HOVER_CLASS,
  SEARCH_FOCUS_CLASS,
  type ScrollAffordance,
} from './OrgProgramSwitcherParts'

export type OrgProgramSwitcherProps = {
  organizations: readonly SwitcherOrganization[]
  selection: SwitcherSelection | null
  loading?: boolean
  onSelectOrg: (organization: SwitcherOrganization) => void
  onSelectProgram: (organization: SwitcherOrganization, programId: string) => void
  triggerClassName?: string
  className?: string
}

function selectionLabel(selection: SwitcherSelection | null): {
  orgLabel: string
  programLabel: string
} {
  if (!selection) {
    return { orgLabel: 'Select organization', programLabel: 'Select program' }
  }
  if (selection.mode === 'org') {
    return { orgLabel: selection.organizationName, programLabel: 'All Programs' }
  }
  return { orgLabel: selection.organizationName, programLabel: selection.programName }
}

export default function OrgProgramSwitcher({
  organizations,
  selection,
  loading = false,
  onSelectOrg,
  onSelectProgram,
  triggerClassName,
  className,
}: Readonly<OrgProgramSwitcherProps>) {
  const selectionOrganizationId =
    selection?.mode === 'org' || selection?.mode === 'program' ? selection.organizationId : null
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const preferredOrgId = selectionOrganizationId ?? organizations[0]?.id ?? null
  const [activeOrgId, setActiveOrgId] = useState<string | null>(preferredOrgId)
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(preferredOrgId)
  const hadSearchQueryRef = useRef(false)
  const orgListRef = useRef<HTMLDivElement>(null)
  const programListRef = useRef<HTMLDivElement>(null)
  const [orgScrollAffordance, setOrgScrollAffordance] =
    useState<ScrollAffordance>(EMPTY_SCROLL_AFFORDANCE)
  const [programScrollAffordance, setProgramScrollAffordance] =
    useState<ScrollAffordance>(EMPTY_SCROLL_AFFORDANCE)

  const updateOrgScrollAffordance = useCallback(() => {
    setOrgScrollAffordance(getScrollAffordance(orgListRef.current))
  }, [])

  const updateProgramScrollAffordance = useCallback(() => {
    setProgramScrollAffordance(getScrollAffordance(programListRef.current))
  }, [])

  const debouncedTrackSearch = useDebouncedCallback((query: string) => {
    trackEvent('client:partner_list:search', {
      query_length: query.length,
      context: 'org_program_switcher',
    })
  }, 300)

  const filtered = useMemo(
    () => buildOrgProgramSwitcherViewModel(organizations, searchQuery),
    [organizations, searchQuery],
  )

  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      return
    }
    setActiveOrgId(preferredOrgId)
    setExpandedOrgId(preferredOrgId)
  }, [open, preferredOrgId])

  useEffect(() => {
    const hasSearchQuery = Boolean(searchQuery.trim())
    const hadSearchQuery = hadSearchQueryRef.current
    hadSearchQueryRef.current = hasSearchQuery

    if (!hasSearchQuery) {
      if (hadSearchQuery) {
        setActiveOrgId(preferredOrgId)
        setExpandedOrgId(preferredOrgId)
      }
      return
    }

    const stillActive = filtered.some((org) => org.id === activeOrgId)
    const stillExpanded = filtered.some((org) => org.id === expandedOrgId)
    const nextId = filtered[0]?.id ?? null
    if (!stillActive && nextId) setActiveOrgId(nextId)
    if (!stillExpanded && nextId) setExpandedOrgId(nextId)
  }, [searchQuery, filtered, activeOrgId, expandedOrgId, preferredOrgId])

  const activeOrg = filtered.find((org) => org.id === activeOrgId) ?? filtered[0] ?? null
  const { orgLabel, programLabel } = selectionLabel(selection)
  const isOrgSelected = selection?.mode === 'org'
  const selectedOrgId = selectionOrganizationId ?? null
  const selectedProgramId = selection?.mode === 'program' ? selection.programId : null
  const hasScrollableDesktopLists =
    filtered.length > 4 || (activeOrg?.visiblePrograms.length ?? 0) > 6

  useEffect(() => {
    if (open) {
      const frame = globalThis.requestAnimationFrame(() => {
        orgListRef.current
          ?.querySelector<HTMLElement>('[data-active="true"]')
          ?.scrollIntoView({ block: 'center' })
        programListRef.current
          ?.querySelector<HTMLElement>('[data-selected="true"]')
          ?.scrollIntoView({ block: 'center' })
        updateOrgScrollAffordance()
        updateProgramScrollAffordance()
      })
      return () => globalThis.cancelAnimationFrame(frame)
    }
    setOrgScrollAffordance(EMPTY_SCROLL_AFFORDANCE)
    setProgramScrollAffordance(EMPTY_SCROLL_AFFORDANCE)
  }, [
    activeOrgId,
    filtered,
    open,
    selectedProgramId,
    updateOrgScrollAffordance,
    updateProgramScrollAffordance,
  ])

  const handleSelectOrg = (orgId: string) => {
    const org = organizations.find((candidate) => candidate.id === orgId)
    if (!org?.canSelectOrg || !org.primaryPartnerId) return
    onSelectOrg(org)
    setOpen(false)
  }

  const handleSelectProgram = (orgId: string, programId: string) => {
    const org = organizations.find((candidate) => candidate.id === orgId)
    if (!org) return
    onSelectProgram(org, programId)
    setOpen(false)
  }

  const handleToggleExpandedOrg = useCallback((orgId: string) => {
    setExpandedOrgId((current) => (current === orgId ? null : orgId))
  }, [])

  return (
    <div className={cn('w-full text-left', className)} id="orgProgramSwitcher">
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={loading}
            data-testid="org-program-switcher-trigger"
            className={cn(
              'flex h-10 w-full items-center justify-between gap-2 rounded-full',
              'bg-cp-special-grey/10 px-4 py-2 text-base text-white',
              'border-none shadow-sm',
              'cursor-pointer',
              FOCUS_VISIBLE_RING,
              'disabled:cursor-not-allowed disabled:opacity-70',
              triggerClassName,
            )}
            aria-label="Organization and program switcher"
          >
            <span className="flex min-w-0 items-center gap-2 overflow-hidden">
              <span className="truncate font-medium">
                {loading && !selection ? 'Loading...' : orgLabel}
              </span>
              <span className="h-2.5 w-px shrink-0 bg-white/30" aria-hidden="true" />
              <span className="truncate font-medium opacity-70">
                {loading && !selection ? '' : programLabel}
              </span>
            </span>
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={8}
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              const input = document.querySelector<HTMLInputElement>(
                '[data-testid="org-program-switcher-search"]',
              )
              input?.focus()
            }}
            className={cn(
              'z-50 w-[min(100vw-2rem,44rem)] rounded-2xl border border-light-grey bg-white p-4 shadow-lg',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            )}
            data-testid="org-program-switcher-panel"
          >
            <div
              className={cn(
                'mb-4 flex h-10 items-center gap-2 rounded border border-light-grey px-2',
                SEARCH_FOCUS_CLASS,
              )}
              data-testid="org-program-switcher-search-wrap"
            >
              <Search
                className="h-5 w-5 shrink-0 text-cp-dark-blue-2"
                aria-hidden="true"
                strokeWidth={1.5}
              />
              <input
                data-testid="org-program-switcher-search"
                type="text"
                role="searchbox"
                autoFocus
                placeholder="Search Organization or Program"
                aria-label="Search organizations or programs"
                value={searchQuery}
                onChange={(event) => {
                  const next = event.target.value
                  setSearchQuery(next)
                  debouncedTrackSearch(next)
                }}
                className="h-full min-w-0 flex-1 appearance-none border-none bg-transparent text-sm text-cp-placeholder outline-none placeholder:text-cp-placeholder focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label="Clear search"
                  data-testid="org-program-switcher-clear-search"
                  onClick={() => setSearchQuery('')}
                  className={cn(
                    'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-cp-dark-blue-2',
                    ROW_HOVER_CLASS,
                    FOCUS_VISIBLE_RING,
                  )}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>

            {filtered.length === 0 ? (
              <div
                className="px-2 py-3 text-sm text-cp-placeholder"
                data-testid="org-program-switcher-no-results"
              >
                No organizations or programs found
              </div>
            ) : (
              <>
                <OrgProgramSwitcherDesktop
                  activeOrg={activeOrg}
                  filtered={filtered}
                  hasScrollableDesktopLists={hasScrollableDesktopLists}
                  isOrgSelected={isOrgSelected}
                  onActivateOrg={setActiveOrgId}
                  onSelectOrg={handleSelectOrg}
                  onSelectProgram={handleSelectProgram}
                  orgListRef={orgListRef}
                  orgScrollAffordance={orgScrollAffordance}
                  programListRef={programListRef}
                  programScrollAffordance={programScrollAffordance}
                  searchQuery={searchQuery}
                  selectedOrgId={selectedOrgId}
                  selectedProgramId={selectedProgramId}
                  updateOrgScrollAffordance={updateOrgScrollAffordance}
                  updateProgramScrollAffordance={updateProgramScrollAffordance}
                />

                <OrgProgramSwitcherMobile
                  expandedOrgId={expandedOrgId}
                  filtered={filtered}
                  isOrgSelected={isOrgSelected}
                  onSelectOrg={handleSelectOrg}
                  onSelectProgram={handleSelectProgram}
                  onToggleOrg={handleToggleExpandedOrg}
                  selectedOrgId={selectedOrgId}
                  selectedProgramId={selectedProgramId}
                />
              </>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  )
}
